import { Component, Input } from '@angular/core';

import { packAddressLines, packDetailRows, packInfoSections } from './event-pack';
import { AppEvent } from './models';

/**
 * The event pack document itself - the markup that becomes the PDF, and nothing else.
 *
 * Separate from any page so the same DOM serves both the download (rendered hidden on the event
 * detail page, exported on click) and the preview route. The element handed to PdfExportService is
 * this component's own, so what prints is exactly what this renders; app chrome must stay outside.
 *
 * Styled twice over, on purpose: EVENT_PACK_CSS is the whole stylesheet of the exported document,
 * while events.scss styles the on-screen preview. Neither reaches the other - see EVENT_PACK_CSS
 * for why a paginated export cannot use the app's own styles.
 */
@Component({
    standalone: false,
    selector: 'abs-event-pack-document',
    template: `
        @if (event) {
            <!-- Read by the exported document's @page footer via string-set; not content, and
                 hidden on screen by events.scss. -->
            <div class="pack-footer-source">
                {{ event.title }} &mdash; {{ 'GLOBAL.EVENTS.PACK.UPDATED_SHORT' | translate }} {{ lastUpdated | date: 'd MMM y' }}
            </div>

            <section class="pack-page pack-cover">
                <h1>{{ event.title }}</h1>
                <p class="pack-cover-meta">
                    {{ event.startDate | date: 'EEEE d MMMM y' }}
                    @if (event.endDate) {
                        &ndash; {{ event.endDate | date: 'EEEE d MMMM y' }}
                    }
                    @if (addressLines.length) {
                        <br />{{ addressLines.join(', ') }}
                    }
                </p>
                @if (event.description) {
                    <abs-markdown class="pack-prose" [source]="event.description"></abs-markdown>
                }
                <p class="pack-muted pack-cover-updated">
                    {{ 'GLOBAL.EVENTS.PACK.LAST_UPDATED' | translate }}: {{ lastUpdated | date: 'd MMM y, HH:mm' }}
                </p>
            </section>

            <section class="pack-page">
                <h2>{{ 'GLOBAL.EVENTS.PACK.DETAILS' | translate }}</h2>
                <table class="pack-rows">
                    @for (row of detailRows; track row.labelKey) {
                        <tr>
                            <th>{{ row.labelKey | translate }}</th>
                            <td>
                                @switch (row.kind) {
                                    @case ('date') {
                                        {{ row.value | date: 'd MMM y' }}
                                    }
                                    @case ('i18n') {
                                        {{ row.value | translate }}
                                    }
                                    @default {
                                        {{ row.value }}
                                    }
                                }
                            </td>
                        </tr>
                    }
                </table>

                @if (event.listSlots?.length) {
                    <h3>{{ 'GLOBAL.EVENTS.DETAIL.LIST_SLOTS' | translate }}</h3>
                    <table class="pack-rows">
                        @for (slot of event.listSlots; track slot.id) {
                            <tr>
                                <th>{{ slot.label || ('GLOBAL.EVENTS.PACK.UNNAMED_LIST' | translate) }}</th>
                                <td>
                                    @if (slot.pointsLimit) {
                                        {{ slot.pointsLimit }}
                                    }
                                    @if (slot.required) {
                                        <span class="pack-muted">({{ 'GLOBAL.EVENTS.DETAIL.REQUIRED' | translate }})</span>
                                    }
                                </td>
                            </tr>
                        }
                    </table>
                }

                @if (event.resultScoringRules) {
                    <h3>{{ 'GLOBAL.EVENTS.PACK.SCORING' | translate }}</h3>
                    <abs-markdown class="pack-prose" [source]="event.resultScoringRules"></abs-markdown>
                }
            </section>

            @if (orderedBlocks.length) {
                <section class="pack-page">
                    <h2>{{ 'GLOBAL.EVENTS.DETAIL.SCHEDULE' | translate }}</h2>
                    @for (block of orderedBlocks; track block.startTime) {
                        <div class="pack-schedule-block">
                            <div class="pack-schedule-head">
                                <span class="pack-schedule-time">{{ block.startTime | date: 'shortTime' }}</span>
                                <span class="pack-schedule-label">{{ block.label }}</span>
                                @if (block.durationMinutes) {
                                    <span class="pack-muted">{{ block.durationMinutes }} {{ 'GLOBAL.MIN' | translate }}</span>
                                }
                            </div>
                            @if (block.description) {
                                <abs-markdown class="pack-schedule-description" [source]="block.description"></abs-markdown>
                            }
                        </div>
                    }
                </section>
            }

            @if (infoSections.length) {
                <section class="pack-page">
                    <h2>{{ 'GLOBAL.EVENTS.PACK.INFORMATION' | translate }}</h2>
                    @for (section of infoSections; track $index) {
                        <div class="pack-info-section">
                            <h3>{{ section.heading }}</h3>
                            @if (section.body) {
                                <abs-markdown class="pack-prose" [source]="section.body"></abs-markdown>
                            }
                        </div>
                    }
                </section>
            }

            <section class="pack-page">
                <h2>{{ 'GLOBAL.EVENTS.PACK.CONTACT' | translate }}</h2>
                @if (addressLines.length) {
                    <h3>{{ 'GLOBAL.EVENTS.DETAIL.LOCATION' | translate }}</h3>
                    <address class="pack-address">
                        @for (line of addressLines; track line) {
                            {{ line }}<br />
                        }
                    </address>
                }
                <table class="pack-rows">
                    @if (event.organiserDisplayName) {
                        <tr>
                            <th>{{ 'GLOBAL.EVENTS.DETAIL.ORGANISER' | translate }}</th>
                            <td>{{ event.organiserDisplayName }}</td>
                        </tr>
                    }
                    @if (event.contactEmail) {
                        <tr>
                            <th>{{ 'GLOBAL.EVENTS.DETAIL.CONTACT' | translate }}</th>
                            <td>{{ event.contactEmail }}</td>
                        </tr>
                    }
                </table>
                @if (event.paymentInstructions) {
                    <h3>{{ 'GLOBAL.EVENTS.PACK.PAYMENT' | translate }}</h3>
                    <abs-markdown class="pack-prose" [source]="event.paymentInstructions"></abs-markdown>
                }
            </section>
        }
    `
})
export class EventPackDocumentComponent {
    /**
     * Recomputed on assignment rather than in getters: the template reads each of these several
     * times, and an Angular template must never call work on every change detection pass.
     */
    @Input()
    set event(value: AppEvent | null | undefined) {
        this.current = value ?? null;
        this.addressLines = value ? packAddressLines(value) : [];
        this.detailRows = value ? packDetailRows(value) : [];
        this.lastUpdated = value?.updatedAt ?? value?.createdAt;
        this.infoSections = value ? packInfoSections(value) : [];
        this.orderedBlocks = [...(value?.scheduleBlocks ?? [])].sort(
            (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );
    }

    get event(): AppEvent | null {
        return this.current;
    }

    private current: AppEvent | null = null;

    addressLines: string[] = [];
    detailRows: ReturnType<typeof packDetailRows> = [];
    /** When the event last changed, falling back to creation - a pack states it so a printed copy
     *  can be told apart from a newer one. */
    lastUpdated?: string;
    orderedBlocks: NonNullable<AppEvent['scheduleBlocks']> = [];
    infoSections: ReturnType<typeof packInfoSections> = [];
}
